import { proxy, subscribe, useSnapshot } from 'valtio'

import { updateUserUiDataSettings } from '@/features/domain/ui'
import { selectRoutingLayout } from '@/features/domain/user'
import { isDeepEqual } from '@/server-data'
import { domainProxy, store } from '@/store'
import { debounce, unproxify } from '@/utils'

type LayoutMode = 'split' | 'map' | 'orders-grid' | 'scheduler'
type RoutingLayout = {
  mode: LayoutMode
  schedulerHeight: number
  bottomElement: 'scheduler' | 'orders-grid'
}
type RoutingLayoutAtom = { layout: RoutingLayout }

// ------------------------------------
// Default values
// ------------------------------------
const defaultRoutingLayout: RoutingLayout = {
  mode: 'split',
  schedulerHeight: 400,
  bottomElement: 'scheduler',
}

// ------------------------------------
// Routing layout atom
// ------------------------------------

function createDefaultRoutingLayout() {
  return { layout: defaultRoutingLayout }
}

const routingLayoutAtom = proxy<RoutingLayoutAtom>(createDefaultRoutingLayout())

export function subscribeRoutingLayout() {
  setRoutingLayout(selectRoutingLayout(store.getState()))

  const unsubscribeDataChangeSuccess = domainProxy.subscribeToNotifications(
    notification => {
      // Ignore notifications that are not related to routing layout changes or are from self
      if (notification.notificationType !== 'routingLayoutChanged' || notification.fromSelf) return

      const newRoutingLayout = selectRoutingLayout(store.getState())
      if (isDeepEqual(newRoutingLayout, unproxify(routingLayoutAtom.layout))) return
      setRoutingLayout(newRoutingLayout)
    },
    ['routingLayoutChanged'],
  )

  const unsubscribeAtom = subscribe(
    routingLayoutAtom.layout,
    debounce(() => {
      const storePreferences = selectRoutingLayout(store.getState())
      if (isDeepEqual(storePreferences, unproxify(routingLayoutAtom.layout))) return

      store.dispatch(
        updateUserUiDataSettings({
          routingLayout: unproxify(routingLayoutAtom.layout),
        }),
      )
    }, 2000),
  )

  return () => {
    unsubscribeDataChangeSuccess()
    unsubscribeAtom()
  }
}

// ------------------------------------
// Write functions
// ------------------------------------

type SetRoutingLayout = (prev: RoutingLayout) => RoutingLayout
type SetRoutingLayoutParam = SetRoutingLayout | Partial<RoutingLayout>

export const setRoutingLayout = (valueOrFunc: SetRoutingLayoutParam) => {
  // callback with prev value
  if (typeof valueOrFunc === 'function') {
    Object.assign(routingLayoutAtom.layout, valueOrFunc(routingLayoutAtom.layout))
  } else {
    // atomic update
    for (const field of Object.keys(valueOrFunc)) {
      routingLayoutAtom.layout[field] = valueOrFunc[field]
    }
  }

  return routingLayoutAtom
}

// ------------------------------------
// React Hooks
// ------------------------------------

export const useRoutingLayout = () => {
  return [useSnapshot(routingLayoutAtom), setRoutingLayout] as const
}
